import React from "react";
import { useSelector } from "react-redux";
import { selectTheme, type ThemeKey } from "../store/themeSlice";
import TwinklingStars from "./TwinklingStars";

const themeClasses: Record<
  ThemeKey,
  { base: string; overlay1: string; overlay2: string }
> = {
  indigo: {
    base: "bg-gradient-to-br from-indigo-900 via-gray-900 to-black",
    overlay1: "from-indigo-500/30",
    overlay2: "from-pink-500/30",
  },
  emerald: {
    base: "bg-gradient-to-br from-emerald-900 via-gray-900 to-black",
    overlay1: "from-emerald-500/30",
    overlay2: "from-cyan-500/30",
  },
  violet: {
    base: "bg-gradient-to-br from-violet-900 via-gray-900 to-black",
    overlay1: "from-violet-500/30",
    overlay2: "from-fuchsia-500/30",
  },
  rose: {
    base: "bg-gradient-to-br from-rose-900 via-gray-900 to-black",
    overlay1: "from-rose-500/30",
    overlay2: "from-orange-500/30",
  },
  slate: {
    base: "bg-gradient-to-br from-slate-900 via-gray-900 to-black",
    overlay1: "from-blue-500/30",
    overlay2: "from-purple-500/30",
  },
  liftoff: {
    base: "bg-gradient-to-br from-indigo-900 via-gray-900 to-black",
    overlay1: "from-indigo-500/20",
    overlay2: "from-indigo-400/15",
  },
  midnight: {
    base: "bg-gradient-to-br from-zinc-950 via-neutral-950 to-black",
    overlay1: "from-slate-400/10",
    overlay2: "from-zinc-500/10",
  },
};

const STARFIELD_GLOW: Partial<Record<ThemeKey, string>> = {
  liftoff:
    "bg-[radial-gradient(ellipse_at_50%_100%,rgba(99,102,241,0.25),transparent_55%)]",
  midnight:
    "bg-[radial-gradient(ellipse_at_50%_0%,rgba(148,163,184,0.14),transparent_50%)]",
};

interface ThemedPageProps {
  children: React.ReactNode;
  className?: string;
  initialTheme?: ThemeKey;
}

/**
 * Full-page layout with themed gradient background.
 * Wrap page content for consistent theme styling across the app.
 */
const ThemedPage: React.FC<ThemedPageProps> = ({
  children,
  initialTheme,
  className = "",
}) => {
  const selectedTheme = useSelector(selectTheme);
  const theme = initialTheme ?? selectedTheme;
  const classes = themeClasses[theme];

  return (
    <div className={`relative w-full text-white ${className}`}>
      <div className={`absolute inset-0 ${classes.base}`} />
      <div
        className={`absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] ${classes.overlay1} via-transparent to-transparent blur-3xl`}
      />
      <div
        className={`absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] ${classes.overlay2} via-transparent to-transparent blur-3xl`}
      />
      {STARFIELD_GLOW[theme] && (
        <>
          <div
            className={`pointer-events-none absolute inset-0 ${STARFIELD_GLOW[theme]}`}
            aria-hidden
          />
          <TwinklingStars />
        </>
      )}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        {children}
      </div>
    </div>
  );
};

export default ThemedPage;
